export type RouletteShareChannel =
  | 'whatsapp'
  | 'facebook'
  | 'instagram'
  | 'tiktok'
  | 'copy'
  | 'copy_message'
  | 'more'

export type RouletteSharePayload = {
  shareUrl: string
  shareTitle: string
  shareDescription: string
}

export type RouletteShareBridgeResult =
  | { status: 'shared'; actionType: RouletteShareChannel }
  | { status: 'opened'; actionType: RouletteShareChannel }
  | { status: 'copied'; actionType: RouletteShareChannel }
  | { status: 'aborted'; actionType: RouletteShareChannel }

export function buildFullShareText(payload: RouletteSharePayload): string {
  return [payload.shareTitle, payload.shareDescription, payload.shareUrl]
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .join('\n\n')
}

export function isMobileShareUserAgent(userAgent?: string): boolean {
  const ua = userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '')
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua)
}

function copyTextWithExecCommand(text: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', 'readonly')
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      textarea.style.pointerEvents = 'none'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      const copied = document.execCommand('copy')
      document.body.removeChild(textarea)
      if (!copied) {
        reject(new Error('copy_failed'))
        return
      }
      resolve()
    } catch (err) {
      reject(err instanceof Error ? err : new Error('copy_failed'))
    }
  })
}

export async function copyTextWithFallback(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return
    } catch {
      await copyTextWithExecCommand(text)
      return
    }
  }
  await copyTextWithExecCommand(text)
}

function openExternalUrl(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer')
}

function openSharePopup(url: string, windowName: string): void {
  const width = 680
  const height = 720
  const left = Math.max(0, Math.round((window.screen.width - width) / 2))
  const top = Math.max(0, Math.round((window.screen.height - height) / 2))
  window.open(
    url,
    windowName,
    `noopener,noreferrer,width=${width},height=${height},left=${left},top=${top}`
  )
}

export async function executeShareBridgeAction(
  actionType: RouletteShareChannel,
  payload: RouletteSharePayload
): Promise<RouletteShareBridgeResult> {
  const encodedUrl = encodeURIComponent(payload.shareUrl)
  const fullText = buildFullShareText(payload)
  const encodedText = encodeURIComponent(fullText)
  const encodedDescription = encodeURIComponent(payload.shareDescription)

  if (actionType === 'whatsapp') {
    openExternalUrl(`https://wa.me/?text=${encodedText}`)
    return { status: 'opened', actionType }
  }

  if (actionType === 'facebook') {
    openSharePopup(
      `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedDescription}`,
      'lpShareFacebook'
    )
    return { status: 'opened', actionType }
  }

  if (actionType === 'instagram') {
    await copyTextWithFallback(payload.shareUrl)
    openExternalUrl('https://www.instagram.com/')
    return { status: 'copied', actionType }
  }

  if (actionType === 'tiktok') {
    await copyTextWithFallback(payload.shareUrl)
    openExternalUrl('https://www.tiktok.com/upload?lang=pt-BR')
    return { status: 'copied', actionType }
  }

  if (actionType === 'copy') {
    await copyTextWithFallback(payload.shareUrl)
    return { status: 'copied', actionType }
  }

  if (actionType === 'copy_message') {
    await copyTextWithFallback(fullText)
    return { status: 'copied', actionType }
  }

  if (actionType === 'more') {
    if (typeof navigator.share !== 'function') {
      throw new Error('Este dispositivo não oferece compartilhamento nativo.')
    }
    try {
      await navigator.share({
        title: payload.shareTitle,
        text: payload.shareDescription,
        url: payload.shareUrl,
      })
      return { status: 'shared', actionType }
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return { status: 'aborted', actionType }
      }
      if (
        typeof error === 'object' &&
        error !== null &&
        'name' in error &&
        (error as { name?: string }).name === 'AbortError'
      ) {
        return { status: 'aborted', actionType }
      }
      throw error instanceof Error
        ? error
        : new Error('Não foi possível abrir o compartilhamento nativo.')
    }
  }

  throw new Error('Ação de compartilhamento inválida.')
}
